import { useState, useEffect } from "react";
import { CSVLink } from "react-csv";
import superagent from 'superagent';
import { toast } from "react-toastify";
import { backendUrl } from '../../../helpers/consts.js';

import { Card, CardContent as CardBody } from "@shadcn/components/ui/card";
import { Col } from "@shadcn/components/ui/col";
import { Row } from "@shadcn/components/ui/row";
import { Button } from "@shadcn/components/ui/button";
import { Input } from "@shadcn/components/ui/input";
import { Label } from "@shadcn/components/ui/label";

const CorpSkuReport = () => {
  const today = new Date();
  const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const [startDate, setStartDate] = useState(formatDate(thirtyDaysAgo));
  const [endDate, setEndDate] = useState(formatDate(today));
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedSkus, setExpandedSkus] = useState([]);

  const toggleSkuExpanded = (sku) => {
    if (expandedSkus.includes(sku)) {
      setExpandedSkus(expandedSkus.filter(s => s !== sku));
    } else {
      setExpandedSkus([...expandedSkus, sku]);
    }
  };

  const toggleAllSkus = () => {
    if (expandedSkus.length === reportData?.data?.length) {
      setExpandedSkus([]);
    } else {
      setExpandedSkus(reportData?.data?.map(item => item.sku) || []);
    }
  };

  const fetchReport = async () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      toast.error("Start date must be before end date");
      return;
    }

    setLoading(true);
    try {
      const response = await superagent
        .get(`${backendUrl}/reports/corp-sku`)
        .query({ startDate, endDate })
        .set('Authorization', `Bearer ${localStorage.getItem("authJWT")}`)
        .set('accept', 'json');

      setReportData(response.body);
      setExpandedSkus([]);
      toast.success("Report loaded successfully");
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error(error.message || "Failed to fetch report data");
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch on mount
  useEffect(() => {
    fetchReport();
  }, []);

  const csvData = reportData?.data ? [
    ['SKU', 'Total Quantity', 'Total Orders'],
    ...reportData.data.map(item => [
      item.sku,
      item.totalQuantity,
      item.totalOrders
    ])
  ] : [];

  const csvDataDetailed = reportData?.data ? [
    ['SKU', 'Order ID', 'Order Name', 'Order Date', 'Quantity', 'Fulfillment Status'],
    ...reportData.data.flatMap(item =>
      item.orders.map(order => [
        item.sku,
        order.orderId,
        order.orderName,
        new Date(order.orderDate).toLocaleDateString(),
        order.quantity,
        order.fulfillmentStatus
      ])
    )
  ] : [];

  const dateString = `${startDate}_to_${endDate}`;

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Row className='mb-6'>
          <Col md={12}>
            <Card>
              <div style={{ margin: '0 12px' }}>
                <h4 className="card-title my-6">Corporate Order SKU Fulfillment Report</h4>

                {/* Date Range Selection */}
                <Row style={{ background: '#fff', padding: '20px', borderTopLeftRadius: 16, borderTopRightRadius: 16 }}>
                  <Col sm="12">
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                      {/* Date Inputs */}
                      <div style={{ flex: '0 0 auto', minWidth: '200px' }}>
                        <Label htmlFor="startDate">Start Date</Label>
                        <Input
                          type="date"
                          id="startDate"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div style={{ flex: '0 0 auto', minWidth: '200px' }}>
                        <Label htmlFor="endDate">End Date</Label>
                        <Input
                          type="date"
                          id="endDate"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          style={{ width: '100%' }}
                        />
                      </div>

                      {/* Generate Button */}
                      <div style={{ flex: '0 0 auto' }}>
                        <Button
                          onClick={fetchReport}
                          disabled={loading}
                          variant="primary"
                          style={{ minWidth: '150px' }}
                        >
                          {loading ? 'Loading...' : 'Generate Report'}
                        </Button>
                      </div>

                      {/* Spacer to push buttons to the right */}
                      <div style={{ flex: '1 1 auto' }}></div>

                      {/* Action Buttons - Only show when data is loaded */}
                      {reportData && (
                        <>
                          <div style={{ flex: '0 0 auto' }}>
                            <Button
                              onClick={toggleAllSkus}
                              variant={expandedSkus.length === reportData?.data?.length ? 'warning' : 'success'}
                              style={{ minWidth: '120px' }}
                            >
                              {expandedSkus.length === reportData?.data?.length ? 'Collapse All' : 'Expand All'}
                            </Button>
                          </div>
                          <div style={{ flex: '0 0 auto', display: 'flex', gap: '8px' }}>
                            <CSVLink
                              data={csvData}
                              filename={`corp_sku_summary_${dateString}.csv`}
                              style={{ textDecoration: 'none' }}
                            >
                              <Button variant="secondary">
                                Export Summary
                              </Button>
                            </CSVLink>
                            <CSVLink
                              data={csvDataDetailed}
                              filename={`corp_sku_detailed_${dateString}.csv`}
                              style={{ textDecoration: 'none' }}
                            >
                              <Button variant="info">
                                Export Detailed
                              </Button>
                            </CSVLink>
                          </div>
                        </>
                      )}
                    </div>
                  </Col>
                </Row>

                {/* Summary Cards */}
                {reportData && (
                  <Row className="mt-4">
                    <Col md={4}>
                      <Card className="bg-primary text-white">
                        <CardBody style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '120px' }}>
                          <h5 className="text-white mb-2">Total SKUs</h5>
                          <h2 className="text-white mb-0">{reportData.summary.totalSkus}</h2>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col md={4}>
                      <Card className="bg-success text-white">
                        <CardBody style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '120px' }}>
                          <h5 className="text-white mb-2">Total Orders</h5>
                          <h2 className="text-white mb-0">{reportData.summary.totalOrders}</h2>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col md={4}>
                      <Card className="bg-info text-white">
                        <CardBody style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '120px' }}>
                          <h5 className="text-white mb-2">Total Quantity</h5>
                          <h2 className="text-white mb-0">{reportData.summary.totalQuantity}</h2>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                )}
              </div>

              <CardBody>
                <div style={{
                  display: 'block',
                  width: '100%',
                  overflowX: 'auto',
                  overflowY: 'scroll',
                  maxHeight: 'calc(60vh)',
                  whiteSpace: 'nowrap'
                }}>
                  {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
                      <div className="spinner-border text-primary m-1" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                    </div>
                  ) : reportData && reportData.data.length > 0 ? (
                    <table className="table mb-0">
                      <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                        <tr>
                          <th>SKU</th>
                          <th>Total Quantity</th>
                          <th>Total Orders</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.data.map((item, index) => {
                          const isExpanded = expandedSkus.includes(item.sku);
                          return (
                            <>
                              <tr key={`sku-${index}`} style={{ cursor: 'pointer', background: '#f8f9fa' }} onClick={() => toggleSkuExpanded(item.sku)}>
                                <td><strong>{item.sku}</strong></td>
                                <td><strong>{item.totalQuantity}</strong></td>
                                <td>{item.totalOrders}</td>
                                <td>
                                  <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); toggleSkuExpanded(item.sku); }}>
                                    {isExpanded ? 'Hide Details' : 'Show Details'}
                                  </Button>
                                </td>
                              </tr>
                              {isExpanded && item.orders.map((order, orderIndex) => (
                                <tr key={`order-${index}-${orderIndex}`} style={{ background: '#ffffff' }}>
                                  <td style={{ paddingLeft: '40px', fontSize: '0.9em' }}>↳ Order: {order.orderName || 'N/A'}</td>
                                  <td style={{ fontSize: '0.9em' }}>{order.quantity}</td>
                                  <td style={{ fontSize: '0.9em' }}>{new Date(order.orderDate).toLocaleDateString()}</td>
                                  <td style={{ fontSize: '0.9em' }}>
                                    <span className={`badge badge-${order.fulfillmentStatus === 'fulfilled' ? 'success' : 'warning'}`}>
                                      {order.fulfillmentStatus}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : reportData && reportData.data.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                      <p>No data found for the selected date range.</p>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                      <p>Select a date range and click "Generate Report" to view data.</p>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default CorpSkuReport;
